import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../context/TranslationContext';
import {
    getNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    getFollowFeed,
    markFollowFeedSeen,
} from '../services/api.shop';
import { Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import { translateWithFallback } from '../utils/translateWithFallback';
import { useFrontendAuth } from '../hooks/useAuth';
import { formatPublicHandle } from '../utils/usernameRules';

type BellTab = 'all' | 'following';

const NotificationBell: React.FC = () => {
    const { t, currentLang, languages } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const notificationLanguage = languages.find((l) => l.code === currentLang)?.translationKey || currentLang;
    const queryClient = useQueryClient();
    const { isAuthenticated } = useFrontendAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [tab, setTab] = useState<BellTab>('all');

    const { data: notifications = [], isLoading, isFetching } = useProtectedQuery({
        queryKey: ['notifications', notificationLanguage],
        queryFn: () => getNotifications(false, notificationLanguage),
        refetchInterval: 30000,
        refetchOnWindowFocus: true,
        staleTime: 1000 * 20,
    });

    const {
        data: feed,
        isLoading: feedLoading,
        isFetching: feedFetching,
    } = useProtectedQuery({
        queryKey: ['followFeed'],
        queryFn: () => getFollowFeed(1, 30),
        enabled: isAuthenticated && isOpen && tab === 'following',
        refetchOnWindowFocus: true,
        staleTime: 1000 * 20,
    });

    // Lightweight unread badge for following (poll with notifications)
    const { data: feedBadge } = useProtectedQuery({
        queryKey: ['followFeedBadge'],
        queryFn: () => getFollowFeed(1, 1),
        enabled: isAuthenticated,
        refetchInterval: 60000,
        staleTime: 1000 * 30,
    });

    const unreadCount = notifications.filter((n) => !n.isRead).length;
    const followUnread = feedBadge?.unreadCount ?? 0;
    const totalBadge = unreadCount + followUnread;
    const displayCount = totalBadge > 9 ? '9+' : totalBadge;

    const markAsReadMutation = useMutation({
        mutationFn: (id: string) => markNotificationAsRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    const markAllAsReadMutation = useMutation({
        mutationFn: markAllNotificationsAsRead,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    const markFeedSeenMutation = useMutation({
        mutationFn: markFollowFeedSeen,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['followFeed'] });
            queryClient.invalidateQueries({ queryKey: ['followFeedBadge'] });
        },
    });

    const formatTimestamp = (dateString: string) => {
        return new Date(dateString).toLocaleString('nl-NL', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Europe/Amsterdam',
        });
    };

    const openFollowingTab = () => {
        setTab('following');
        markFeedSeenMutation.mutate();
    };

    return (
        <div className="relative">
            <button
                type="button"
                data-tour-id="home.notifications"
                onClick={() => setIsOpen(!isOpen)}
                className="min-h-11 min-w-11 p-2 hover:bg-[var(--accent-color)]/20 rounded-full transition-colors relative inline-flex items-center justify-center"
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                <Bell className="w-5 h-5 text-[var(--text-color)]" />
                {totalBadge > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1 shadow-sm">
                        {displayCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 panel-edge-safe w-[min(100vw-1rem,22rem)] md:w-96 bg-[var(--bg-color)] border border-[var(--accent-color)] rounded-2xl shadow-2xl z-50 max-h-[min(480px,70dvh)] flex flex-col overflow-hidden">
                    <div className="px-4 py-3 border-b border-[var(--accent-color)] font-semibold text-[var(--text-color)] flex justify-between items-center flex-shrink-0">
                        <span>{t('notifications.title')}</span>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="text-[var(--text-color)]/60 hover:text-[var(--text-color)] text-xl leading-none min-h-11 min-w-11"
                        >
                            ×
                        </button>
                    </div>

                    <div className="flex border-b border-[var(--accent-color)]/40 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setTab('all')}
                            className={`flex-1 px-3 py-2.5 text-sm font-medium transition ${
                                tab === 'all'
                                    ? 'border-b-2 border-[var(--accent-color)] text-[var(--accent-color)]'
                                    : 'text-[color-mix(in_srgb,var(--text-color),transparent_40%)]'
                            }`}
                        >
                            {tr('follow.tab.all', 'All')}
                            {unreadCount > 0 && (
                                <span className="ml-1 text-xs opacity-70">({unreadCount})</span>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={openFollowingTab}
                            className={`flex-1 px-3 py-2.5 text-sm font-medium transition ${
                                tab === 'following'
                                    ? 'border-b-2 border-[var(--accent-color)] text-[var(--accent-color)]'
                                    : 'text-[color-mix(in_srgb,var(--text-color),transparent_40%)]'
                            }`}
                        >
                            {tr('follow.tab.following', 'Following')}
                            {followUnread > 0 && (
                                <span className="ml-1 text-xs opacity-70">({followUnread})</span>
                            )}
                        </button>
                    </div>

                    <div className="overflow-y-auto flex-1 max-h-[320px] px-1">
                        {tab === 'all' && (
                            <>
                                {isLoading && notifications.length === 0 ? (
                                    <div className="p-8 text-center text-[var(--text-color)]/60">
                                        {t('state.loading')}
                                    </div>
                                ) : notifications.length === 0 ? (
                                    <div className="p-8 text-center text-[var(--text-color)]/60">
                                        {t('notifications.empty')}
                                    </div>
                                ) : (
                                    notifications.map((notif) => (
                                        <Link
                                            key={notif.id}
                                            to={notif.link || '#'}
                                            onClick={() => markAsReadMutation.mutate(notif.id)}
                                            className={`block px-4 py-4 border-b border-[var(--accent-color)]/30 last:border-none hover:bg-[var(--accent-color)]/10 transition-colors ${
                                                notif.isRead ? 'opacity-75' : 'font-medium'
                                            }`}
                                        >
                                            <p className="text-sm text-[var(--text-color)] leading-snug">
                                                {notif.message}
                                            </p>
                                            <p className="text-xs text-[var(--text-color)]/60 mt-2">
                                                {formatTimestamp(notif.createdAt)}
                                            </p>
                                        </Link>
                                    ))
                                )}
                            </>
                        )}

                        {tab === 'following' && (
                            <>
                                {feedLoading && !feed ? (
                                    <div className="p-8 text-center text-[var(--text-color)]/60">
                                        {t('state.loading')}
                                    </div>
                                ) : !feed?.items?.length ? (
                                    <div className="p-8 text-center text-[var(--text-color)]/60">
                                        {tr('follow.feed.empty', 'Follow sellers to see their new listings here.')}
                                    </div>
                                ) : (
                                    feed.items.map((item) => (
                                        <Link
                                            key={item.eventId}
                                            to={`/products/${item.productId}`}
                                            onClick={() => setIsOpen(false)}
                                            className="flex gap-3 px-4 py-3 border-b border-[var(--accent-color)]/30 last:border-none hover:bg-[var(--accent-color)]/10 transition-colors"
                                        >
                                            {item.primaryImageUrl ? (
                                                <img
                                                    src={item.primaryImageUrl}
                                                    alt=""
                                                    className="h-14 w-14 shrink-0 rounded-lg object-cover"
                                                />
                                            ) : (
                                                <div className="h-14 w-14 shrink-0 rounded-lg bg-[var(--accent-color)]/20" />
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs text-[var(--accent-color)] truncate">
                                                    {formatPublicHandle(item.sellerDisplayName)}
                                                </p>
                                                <p className="text-sm font-medium text-[var(--text-color)] leading-snug line-clamp-2">
                                                    {item.productTitle}
                                                    {item.isSold && (
                                                        <span className="ml-1 text-xs font-normal text-[var(--text-color)]/50">
                                                            ({tr('follow.feed.sold', 'Sold')})
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-xs text-[var(--text-color)]/60 mt-1">
                                                    €{Number(item.price).toFixed(2)} ·{' '}
                                                    {formatTimestamp(item.listedAt)}
                                                </p>
                                            </div>
                                        </Link>
                                    ))
                                )}
                            </>
                        )}
                    </div>

                    {((tab === 'all' && isFetching && notifications.length > 0) ||
                        (tab === 'following' && feedFetching && (feed?.items?.length ?? 0) > 0)) && (
                        <div className="text-center text-[10px] text-[var(--text-color)]/50 py-1 border-t border-[var(--accent-color)]/20">
                            {t('notifications.updating') || 'Updating...'}
                        </div>
                    )}

                    {tab === 'all' && notifications.length > 0 && (
                        <div className="flex-shrink-0 border-t border-[var(--accent-color)]/30 bg-[var(--bg-color)] pt-3">
                            <div className="px-4 pb-2">
                                <div className="h-px w-full bg-[var(--accent-color)]/50 mb-2" />
                                <div className="text-center text-xs text-[var(--text-color)]/50">
                                    {t('notifications.scroll')}
                                </div>
                            </div>
                            <button
                                onClick={() => markAllAsReadMutation.mutate()}
                                className="w-full py-3 text-sm font-medium text-[var(--text-color)] hover:bg-[var(--accent-color)]/10 transition-colors border-t border-[var(--accent-color)]/20 min-h-11"
                            >
                                {t('notifications.markasread')}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
